import { JSX, useContext } from "react";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";

const AdequacyImpactDescription = (): JSX.Element => {
  const ffContext = useContext(FeatureFlagContext);
  const criticalAlertsEnabled = ffContext.enabledFeatureFlags.has(Features.NETWORK_INTEGRITY_ADEQUACY_IMPACT_CRITICAL_ALERTS);

  if (criticalAlertsEnabled) {
    return <>
      <p>
        The Adequacy Impact card showcases the future impact of terminating provider access points from the network that have network integrity alerts or warnings associated with them.
        Understanding how these removals will impact future Network Adequacy results plays a key role in maintaining future compliance and member satisfaction expectations.
      </p>
      <ul>
        <li>The <b>Current Gaps dotted line</b> represents the current number of Network Adequacy gaps in the analysis, matching the Adequacy tab and providing a baseline context for the three bars on the chart.</li>
        <li>The <b>Critical Alerts Removed bar</b> represents the Adequacy analysis results if all access points with a critical alert were terminated from the network.</li>
        <li>The <b>All Alerts Removed bar</b> represents the Adequacy analysis results if all access points with either a critical alert or an attestation-based alert were terminated from the network.</li>
        <li>The <b>Alerts and Warnings Removed bar</b> represents the Adequacy analysis results if all access points with either a critical alert, an attestation-based alert, or an excess warning were terminated from the network.</li>
      </ul>
      <p>
        Counts reflect <b>required specialties</b> in <b>current counties</b> (Active, Pending, and Approved) within the applied service area only. Opportunity counties and informational specialties are excluded.
      </p>
      <p>
        Click any bar to open the <b>Adequacy Impact page</b> and see the detailed information powering the bar chart. The Adequacy Impact page outlines gaps for each scenario and allows for filtering by geographic region, specialty, and toggling between only the new gaps or the complete list of current and new gaps combined.
      </p>
      <p>
        On the Adequacy Impact page, the <b>View Details flyout</b> shows additional details on the access points removed that contributed to each new gap, and the Network Integrity issue(s) associated with those providers.
        <i> (Note: the View Details flyout for <b>All Alerts Removed</b> or <b>Alerts and Warnings Removed</b> bars are only available for users with an active QES™ Accuracy license.)</i>
      </p>
      <p>
        <i>Note: This card represents the same results when viewing <b>All Network Providers</b> and <b>Servicing Network Providers</b> and is not available on the <b>All Prospect Providers</b> or <b>Servicing Prospect Providers</b> view.</i>
      </p>
    </>;
  }

  return <>
    <p>
      The Adequacy Impact card showcases the impact of terminating providers from the network that have network integrity alerts or warnings associated with them.
      Understanding how removing these providers will impact future Adequacy results plays a key role in maintaining future compliance and member satisfaction expectations.
    </p>
    <ul>
      <li>The <b>Current Gaps bar</b> represents the Adequacy analysis “as-is” meaning all providers with Critical alerts, Attestation-based alerts, or Excess warnings remain in the network.</li>
      <li>The <b>Alerts Removed bar</b> represents the Adequacy analysis results if all providers with a Critical alert or Attestation-based alert were terminated from the network.</li>
      <li>The <b>Alerts and Warnings Removed bar</b> represents the Adequacy analysis results if all providers with a Critical alert, Attestation-based alert, or Excess warning were terminated from the network.</li>
    </ul>
    <p>
      Adequacy Impact gap counts are based on required specialties in active counties within the applied service area, meaning opportunity counties or informational specialties are not included in the counts.
    </p>
    <p>
      For users with an active QES™ Accuracy license, additional details on which gaps would open, the providers removed that contributed to each new gap, and the Network Integrity issues associated with those providers can be accessed by clicking on the <b>Alerts Removed</b> or <b>Alerts and Warnings Removed</b> bars.
    </p>
    <p>
      <i>Note: This card represents the same results when viewing <b>All Network Providers</b> and <b>Servicing Network Providers</b> and is not available on the <b>All Prospect Providers</b> view.</i>
    </p>
  </>;
}

export { AdequacyImpactDescription };